import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MODO_POR_DEFECTO, ModoTiendas } from '../utils/modoTiendas';
// Manrope (300-800) para todo el tablero hi-fi, tal como especifica el
// design handoff. Se registra vía @fontsource (evita depender de un CDN de
// Google Fonts que puede estar bloqueado por CSP).
import '@fontsource/manrope/300.css';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/manrope/800.css';

import Header, { IndicadoresView } from '../components/Header';
import { EstadoCarga, EstadoError } from '../components/EstadoCaja';
import VentaCards, { NOTA_CLIENTES_GLOBAL, NOTA_CLIENTES_TIENDA, NOTA_TOTAL_TIENDA } from '../components/VentaCards';
import DetalleNegadoModal from '../components/global/DetalleNegadoModal';
import Embudo from '../components/global/Embudo';
import Filtros, { FiltrosAplicados } from '../components/global/Filtros';
import Medidores from '../components/global/Medidores';
import NegadosCards from '../components/global/NegadosCards';
import AvanceMeta from '../components/meta/AvanceMeta';
import AvisoCobertura from '../components/meta/AvisoCobertura';
import GraficoMeta from '../components/meta/GraficoMeta';
import MedidoresMeta from '../components/meta/MedidoresMeta';
import TablaMeta from '../components/meta/TablaMeta';
import TablaSucursales from '../components/global/TablaSucursales';
import BodegaCard from '../components/tienda/BodegaCard';
import DrillTienda, { TiendaAplicada } from '../components/tienda/DrillTienda';
import GraficoHora from '../components/tienda/GraficoHora';
import KpisHoy from '../components/tienda/KpisHoy';
import NegadosDrillModal from '../components/tienda/NegadosDrillModal';
import NegadosTiendaTable from '../components/tienda/NegadosTiendaTable';
import Vendedores from '../components/tienda/Vendedores';
import VendedorDetalleModal from '../components/tienda/VendedorDetalleModal';
import { cadenaToChain, storeChainBranch } from '../data/stores';
import { useCatalogoTiendas } from '../hooks/useCatalogoTiendas';
import { useEsMovil } from '../hooks/useEsMovil';
import type { Store } from '../data/stores';
import { IndicadoresService, statusDeError } from '../services/indicadores.service';
import { colors, fontFamily, keyframesCss } from '../styles/tokens';
import { getNowHourMx } from '../utils/chart';
import { todayRange, totalizarMeta } from '../utils/meta';
import type {
  DashboardFiltros,
  MetaResponse,
  NegadoProductoRow,
  NegadoTallaRow,
  SucursalRow,
  TiendaResponse,
} from '../types/indicadores.types';
import { aggregateGlobal, defaultDateRange, rowKey } from '../utils/aggregate';
import type { DateRange } from '../utils/aggregate';
import {
  applyTienda,
  claveFiltros,
  computeFilteredStores,
  computeScopeLabel,
  defaultGlobalFiltros,
  GlobalFiltros,
} from '../utils/filtros';
import {
  clavePeriodo,
  hoyIso,
  mismoPeriodo,
  Periodo,
  PERIODO_VIVO,
  periodoPorDefecto,
  periodoRango,
  rangoEfectivo,
} from '../utils/periodo';
import { buildSucursalTableRows, PREFIJO_FUERA_DE_CATALOGO } from '../utils/tabla';
import { cascadaVacia, catalogoEfectivo } from '../utils/catalogoEfectivo';
import { defaultStoreId } from '../utils/tienda';

/**
 * Meta de efectividad de atención para el ranking de vendedores + coaching de
 * Mi tienda (Task 10). Constante de módulo, no configurable desde una UI de
 * settings (handoff línea 350/266: `conversionGoal` prop con default `65` —
 * ver `task-10-brief.md`, "no need for a settings UI").
 */
const CONVERSION_GOAL = 65;

/**
 * Espera antes del reintento automático de Venta (HF Fix 3): el primer
 * `GET /dashboard/sucursales` de un rango nuevo puede traer TODOS los
 * `paresVendidos` en `null` porque el ERP no respondió a tiempo y el caché
 * está frío. 15s le da margen de sobra a ese caché para calentarse en segundo
 * plano sin encimarse con el fetch original ni "machacar" Proscai.
 */
const VENTA_RETRY_DELAY_MS = 15_000;
// Ítem 4 (2026-07-28): el reintento de `/dashboard/sucursales` es un POLL
// acotado: re-pide cada `VENTA_RETRY_DELAY_MS` hasta que la venta deje de
// estar en null y la API deje de marcar las altas como pendientes, o hasta
// `SUC_MAX_RETRIES`.
const SUC_MAX_RETRIES = 12;

/**
 * Espera antes del reintento automático de `clientesNuevosPendiente` de
 * `/dashboard/tienda` (espejo de `VENTA_RETRY_DELAY_MS`): la consulta lenta de
 * altas (`fcli.CLIALTA`) degrada a "pendiente" y el calentador
 * (`dashboard-venta-warm`) la precalienta en background. A lo más UN reintento.
 */
const CLIENTES_NUEVOS_RETRY_DELAY_MS = 15_000;

/**
 * "Sin colaborador elegido" como constante de módulo, NO un `[]` nuevo en cada
 * uso: los efectos dependen de la llave de esta lista y una referencia estable
 * deja que React haga bail-out del `setState`.
 */
const SIN_COLABORADORES: string[] = [];

/** Llave estable de una lista de ids (como CONJUNTO) para dependencias de efectos. */
const claveLista = (xs: string[]) => [...xs].sort().join('\u0001');

/*
 * ⚠️ AQUÍ HABÍA UNA CONSTANTE `PRESUPUESTO_VISIBLE = false`.
 *
 * Se apagó para que la pestaña no saliera en producción, donde `GET
 * /dashboard/meta` todavía no existe (llega con `1a8372b`) y pulsarla habría
 * dado un 404 a la vista de todos. Pero **el bundle del admin es el mismo para
 * los dos entornos**, así que apagarla en producción la apagó también en
 * desarrollo, donde el endpoint SÍ estaba y llevaba días sirviendo datos
 * buenos. Nadie lo notó hasta que el usuario fue a buscar la pestaña.
 *
 * Ahora se le pregunta a la API (`IndicadoresService.hayPresupuesto`). Medido el
 * 2026-08-31: `GET /dashboard/meta/estado` da **200 en desarrollo y 404 en
 * producción**, que es exactamente la distinción que hacía falta. Además la
 * pestaña se encenderá sola el día que la API llegue a producción, sin que nadie
 * tenga que acordarse de volver aquí.
 */

/**
 * ¿Se le muestra la pestaña de Presupuesto a quien entra al tablero?
 *
 * No, todavía: la fase 1 se sigue validando y no debe aparecerle a nadie que no
 * la esté buscando. Se entra por `/indicadores?vista=presupuesto`. Para
 * lanzarla, este `false` pasa a `true` y la pestaña aparecerá **sola** en cada
 * entorno cuya API tenga el backend — no hay nada más que tocar.
 *
 * ⚠️ Esto NO es el `PRESUPUESTO_VISIBLE` que apagó los dos entornos el
 * 2026-08-31, y la diferencia importa. Aquella constante intentaba responder
 * «¿existe el backend aquí?», que **depende del entorno**, y por eso un valor
 * compilado no podía contestarla: apagó también desarrollo, donde sí estaba.
 * Ésta responde «¿está lanzado el producto?», que es la MISMA respuesta en
 * todos los entornos y por tanto sí es una constante legítima. La pregunta que
 * depende del entorno la sigue haciendo el sondeo (`hayPresupuesto`), y las dos
 * condiciones se exigen juntas. Ver
 * `docs/lessons/2026-08-31-el-interruptor-que-apago-los-dos-entornos.md`.
 */
const PESTANA_PRESUPUESTO_LANZADA = false;

/**
 * ¿La URL pide la vista de Presupuesto? `/indicadores?vista=presupuesto`.
 *
 * Se lee de `window.location` y no del router a propósito: es un acceso lateral
 * y temporal, y no merece meter un parámetro en las rutas del admin que luego
 * haya que retirar. Devuelve `false` ante cualquier cosa rara, incluido un
 * entorno sin `window` (las pruebas de nodo).
 */
export function pidePresupuestoPorUrl(): boolean {
  try {
    return (
      new URLSearchParams(window.location.search).get('vista') === 'presupuesto'
    );
  } catch {
    return false;
  }
}

/**
 * "hace X min" legible. Por debajo de un minuto se dice "ahora": un
 * "hace 0 min" se lee como un error de la pantalla, no como frescura.
 */
function textoFrescura(minutos: number): string {
  const m = Math.max(0, Math.round(minutos));
  if (m < 1) return 'ahora';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  return h === 1 ? 'hace 1 h' : `hace ${h} h`;
}

/** Error de un bloque del tablero, listo para `EstadoError`. */
interface ErrorVista {
  mensaje: string;
  /** La API rechazó el periodo (400): no se ofrece «Reintentar». */
  validacion: boolean;
}

/** Mensaje de un error capturado, con un texto de respaldo en español. */
function mensaje(err: unknown, respaldo: string): string {
  return err instanceof Error && err.message ? err.message : respaldo;
}

function errorDe(err: unknown, respaldo: string): ErrorVista {
  return { mensaje: mensaje(err, respaldo), validacion: statusDeError(err) === 400 };
}

/**
 * Shell del tablero hi-fi "Colectivo Moda": `Header` con las pestañas
 * Global / Tienda (y Presupuesto, por URL) y, debajo, la vista activa.
 *
 * ⚠️ ESTADO APLICADO, NO BORRADOR (SPEC `2026-10-12-tableros-homologados.md`).
 * Todo lo que consulta —los efectos de fetch, los modales, `negadosRango`—
 * lee SÓLO lo aplicado: `periodo`, `filtros` (Global) y tienda + colaborador
 * (Tienda). Los paneles (`Filtros`, `DrillTienda`) editan un borrador propio y
 * lo devuelven completo con «Aplicar». Antes cada tecla en una fecha disparaba
 * 2-3 consultas, casi todas con años absurdos (0002, 0020, 0202…), y el 11-oct
 * dejó 38 respuestas 500 en producción.
 *
 * ⚠️ UN SOLO PERIODO para Global y Tienda: `{ modo: 'vivo' }` o un rango.
 * Cambiar de pestaña lo conserva en los dos sentidos, y «Hoy (en vivo)» es el
 * vivo en las dos (Tienda con el marcador «ahora» y los deltas «vs ayer»).
 * Presupuesto lleva su periodo aparte: su «mes en curso» llega a fin de mes,
 * días que Global y Tienda no pueden pedir.
 *
 * Vista Global: un fetch de `/dashboard/sucursales` por (periodo, estatus) y la
 * cascada Regional/Supervisor/Cadena/Tienda se aplica client-side sobre
 * `filteredStores`. Las cards de negados tienen su propio fetch. Clic en una
 * fila de la tabla filtra el Global por esa tienda (aplicado al instante).
 *
 * Vista Tienda: la tienda aplicada arranca en la default del handoff
 * (`defaultStoreId`, `MARCHA#59`) o la que traiga el Global filtrado al entrar.
 * Un fetch de `/dashboard/tienda` trae los KPIs, la curva por hora, bodega y
 * vendedores; los negados de la tienda tienen el suyo.
 */
const IndicadoresScreen: React.FC = () => {
  // Disposición de teléfono. El módulo entero está escrito con estilos EN
  // LÍNEA, que no admiten `@media`: el punto de corte se decide aquí y cada
  // bloque elige su disposición. Ver `hooks/useEsMovil`.
  const esMovil = useEsMovil();
  const [view, setView] = useState<IndicadoresView>('global');

  // ===== Estado APLICADO =====================================================
  // Global: la cascada + estatus de producto. Vacío = sin filtro.
  const [filtros, setFiltros] = useState<GlobalFiltros>(defaultGlobalFiltros);
  // El periodo compartido por Global y Tienda. Abre en la semana anterior
  // completa (lun-dom): un periodo cerrado y comparable con el reporte semanal.
  const [periodo, setPeriodo] = useState<Periodo>(() => periodoPorDefecto());
  // Presupuesto, aparte (ver el comentario del componente).
  const [rangoMeta, setRangoMeta] = useState<DateRange>(() => defaultDateRange());

  // --- Frescura del "en vivo" de la venta del día ------------------------
  //
  // `undefined` = todavía no se ha preguntado; `null` = no hay materializado y
  // la venta sale en vivo del ERP. Se distinguen porque no significan lo mismo
  // y el chip dice una cosa distinta en cada caso.
  const [frescuraHoy, setFrescuraHoy] = useState<
    { minutos: number; fresco: boolean; tiendas: number } | null | undefined
  >(undefined);

  // ¿Esta API sabe de Presupuesto? Se pregunta una vez al montar.
  //
  // ⚠️ LA PESTAÑA YA NO SE PINTA NUNCA (`onMeta` va sin definir más abajo).
  // Presupuesto está montado y sirviendo datos, pero se entra SÓLO por URL:
  //
  //     /indicadores?vista=presupuesto
  //
  // Es una decisión de producto, no técnica: la fase 1 se sigue validando y no
  // debe aparecerle todavía a quien entra al tablero. Cuando se quiera abrir,
  // el cambio es una línea — devolverle a `onMeta` su función — y la pestaña
  // aparecerá sola en los entornos cuya API tenga el backend.
  //
  // La URL NO es el permiso, es sólo la intención. El permiso lo sigue dando el
  // sondeo: si la API del entorno no tiene el backend (producción hoy responde
  // 404), pedir la vista por URL pintaría una pantalla vacía con filtros que no
  // llevan a ningún sitio. Por eso se entra a 'meta' únicamente cuando el
  // sondeo dice que sí; si no, se queda en Global sin avisar de nada, que es lo
  // que le corresponde a una URL que alguien no debería tener.
  const [hayPresupuesto, setHayPresupuesto] = useState(false);
  useEffect(() => {
    let vivo = true;
    IndicadoresService.hayPresupuesto().then((r) => {
      if (!vivo) return;
      setHayPresupuesto(r);
      if (r && pidePresupuestoPorUrl()) setView('meta');
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Switch "Activas / Sin uso / Todas" del Header (sólo Global). ⚠️ Acota
  // SÓLO la operación —embudo operativo, medidores y tabla—; la venta, las
  // altas y los estrenos son siempre los de la cascada (decisión del usuario,
  // 12-oct; ver `aggregateGlobal`). Es un filtro client-side sobre datos ya
  // cargados: no pasa por «Aplicar» porque no consulta nada.
  const [modo, setModo] = useState<ModoTiendas>(MODO_POR_DEFECTO);

  // Catálogo de tiendas ACTIVAS servido por el backend (2026-08-11). Proscai
  // manda; `stores.ts` sólo queda de respaldo dentro del hook si la API falla.
  const {
    stores: catalogoStores,
    cerradas: catalogoCerradas,
    cargando: cargandoCatalogo,
    usandoRespaldo: catalogoDeRespaldo,
  } = useCatalogoTiendas();

  // ----- Rango efectivo del Global y llaves de dependencias -----------------
  // `/sucursales` y `negados-*` siempre exigen `desde`/`hasta`: el vivo es hoy.
  const hoy = hoyIso();
  const rangoGlobal = rangoEfectivo(periodo, hoy);
  const enVivo = periodo.modo === 'vivo';
  const claveEstatus = claveLista(filtros.estatus);

  // Catálogo EFECTIVO del periodo aplicado (SPEC 2026-10-13, punto b): las
  // activas más las CERRADAS que operaron en el periodo («MR CELAYA (cerrada)»
  // en septiembre, nada en vivo). Es el de la cascada de Global; Tienda sigue
  // con las activas (abrir una cerrada en Tienda queda fuera de este paso).
  const catalogoGlobal = useMemo(
    () => catalogoEfectivo(catalogoStores, catalogoCerradas, rangoGlobal.desde),
    [catalogoStores, catalogoCerradas, rangoGlobal.desde],
  );
  // Cascada vacía = «Todas»: entran TODOS los renglones de la API, también los
  // que ningún catálogo ubica. Global sin cascada = suma de los renglones.
  const sinCascada = cascadaVacia(filtros);

  // `filteredStores` es la fuente para el embudo/medidores/tabla: la cascada
  // APLICADA completa (regional + supervisor + cadena + tienda).
  const filteredStores = useMemo(
    () => computeFilteredStores(catalogoGlobal, filtros),
    [catalogoGlobal, filtros],
  );

  /**
   * «Aplicar» del panel de Global. Sólo cambia lo que de verdad cambió: un
   * objeto nuevo con el mismo contenido no debe re-disparar consultas.
   */
  const aplicarGlobal = ({ filtros: f, periodo: p }: FiltrosAplicados) => {
    setFiltros((prev) => (claveFiltros(prev) === claveFiltros(f) ? prev : f));
    setPeriodo((prev) => (mismoPeriodo(prev, p) ? prev : p));
  };

  // Ítem 5 (2026-07-28): clic en una fila del "Detalle operativo por sucursal"
  // FILTRA el Global por esa tienda. Es una acción directa, como un atajo: se
  // aplica al instante (y el panel reinicia su borrador a lo aplicado).
  const handleOpenStore = (storeId: string) => {
    // Un renglón «(fuera de catálogo)» no es una tienda que se pueda filtrar.
    if (storeId.startsWith(PREFIJO_FUERA_DE_CATALOGO)) return;
    setFiltros((f) => applyTienda(f, [storeId]));
  };

  useEffect(() => {
    if (!enVivo || view !== 'global') {
      setFrescuraHoy(undefined);
      return;
    }
    let vivo = true;
    const pedir = () =>
      IndicadoresService.getVentaHoyEstado().then((r) => {
        if (vivo) setFrescuraHoy(r);
      });
    pedir();
    // El materializador corre cada 30 min; preguntar cada 5 mantiene el "hace
    // X min" honesto sin coste (no toca el ERP, sólo Postgres).
    const t = setInterval(pedir, 5 * 60 * 1000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [enVivo, view]);

  const [sucursalRows, setSucursalRows] = useState<SucursalRow[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [errorSucursales, setErrorSucursales] = useState<ErrorVista | null>(null);
  // «Reintentar» del error: cambiar el contador vuelve a correr el efecto.
  const [intentoSucursales, setIntentoSucursales] = useState(0);

  // Reintentos automáticos hechos para el rango ACTUAL (poll acotado de abajo).
  const [sucRetryCount, setSucRetryCount] = useState(0);

  // Llave de lo último que se cargó en Global: volver de Tienda con el MISMO
  // periodo y estatus no vuelve a pedir nada (como antes, cuando Global no
  // dependía de la pestaña).
  const cargadoSucursales = useRef<string | null>(null);

  useEffect(() => {
    // Sólo se consulta lo que se está viendo: con el periodo compartido, aplicar
    // un rango en Tienda ya no debe pedir también el Global.
    if (view !== 'global') return;
    const clave = `${rangoGlobal.desde}|${rangoGlobal.hasta}|${claveEstatus}|${intentoSucursales}`;
    if (cargadoSucursales.current === clave) return;

    let cancelled = false;
    setLoadingSucursales(true);
    setErrorSucursales(null);
    setSucRetryCount(0);

    IndicadoresService.getSucursales({
      desde: rangoGlobal.desde,
      hasta: rangoGlobal.hasta,
      status: filtros.estatus,
    })
      .then((rows) => {
        if (!cancelled) {
          setSucursalRows(rows);
          cargadoSucursales.current = clave;
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorSucursales(errorDe(err, 'No se pudo cargar el periodo.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSucursales(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, rangoGlobal.desde, rangoGlobal.hasta, claveEstatus, intentoSucursales]);

  // Agregación client-side (embudo + 3 medidores + venta): ver `utils/aggregate.ts`.
  const aggregate = useMemo(
    () => aggregateGlobal(sucursalRows, filteredStores, modo, sinCascada),
    [sucursalRows, filteredStores, modo, sinCascada],
  );

  // Chip de alcance data-driven (pendiente #5): "tienda activa en la
  // herramienta" = tuvo >= 1 solicitud ATENDIDA (`atendidas > 0`) en el
  // periodo — NO el tamaño del catálogo filtrado.
  const activeStoreKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of sucursalRows) {
      if (row.atendidas > 0) {
        keys.add(rowKey(row.chainId, row.branchOfficeId));
      }
    }
    return keys;
  }, [sucursalRows]);

  const activeCount = filteredStores.filter((s) => {
    const { chain, branchOffice } = storeChainBranch(s);
    return activeStoreKeys.has(rowKey(chain, branchOffice));
  }).length;

  const scopeLabel = computeScopeLabel(catalogoGlobal, filtros, filteredStores, {
    modo,
    activeCount,
  });

  // ===== Reintento automático de Venta y altas (Proscai) — HF Fix 3 + ítem 4 =====
  // Si `/sucursales` llega con la venta en null (ERP frío) o con las altas
  // marcadas como pendientes por la API, se vuelve a pedir el MISMO rango cada
  // 15 s, hasta `SUC_MAX_RETRIES`. Cualquier cambio de lo aplicado cancela el
  // timer pendiente para que una respuesta tardía no pise datos más nuevos.
  const claveFiltrosAplicados = claveFiltros(filtros);
  useEffect(() => {
    let cancelled = false;

    const pendiente = aggregate.ventaUnavailable || aggregate.clientesNuevosPendiente;
    if (
      view !== 'global' ||
      loadingSucursales ||
      errorSucursales ||
      !pendiente ||
      sucRetryCount >= SUC_MAX_RETRIES
    ) {
      return () => {
        cancelled = true;
      };
    }

    const { desde, hasta } = rangoGlobal;
    const status = filtros.estatus;
    const timer = setTimeout(() => {
      IndicadoresService.getSucursales({ desde, hasta, status })
        .then((rows) => {
          if (!cancelled) {
            setSucursalRows(rows);
          }
        })
        .catch(() => {
          // Se rinde en silencio: deja las filas ya mostradas tal cual.
        })
        .finally(() => {
          if (!cancelled) {
            setSucRetryCount((c) => c + 1);
          }
        });
    }, VENTA_RETRY_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    rangoGlobal.desde,
    rangoGlobal.hasta,
    claveFiltrosAplicados,
    aggregate.ventaUnavailable,
    aggregate.clientesNuevosPendiente,
    sucRetryCount,
    loadingSucursales,
    errorSucursales,
  ]);

  // `true` mientras siga pendiente el poll automático: "calculando…" en vez de "N/D".
  const reintentosVivos = sucRetryCount < SUC_MAX_RETRIES;
  const ventaCalculando = aggregate.ventaUnavailable && reintentosVivos;

  // Tabla "Detalle operativo por sucursal": mismo join (chain, branchOffice)
  // que el embudo, por fila; acotada por el modo (es operación).
  const sucursalTable = useMemo(
    () => buildSucursalTableRows(sucursalRows, filteredStores, modo, sinCascada),
    [sucursalRows, filteredStores, modo, sinCascada],
  );

  // Cards de negados: los endpoints aceptan UNA `chain` y UNA `branchOffice`,
  // así que sólo se acotan server-side con exactamente una cadena o una tienda
  // elegidas. Regional, Supervisor, varias tiendas o varias cadenas no viajan:
  // las tarjetas lo dicen en su rótulo (`alcanceNegados`).
  const selectedStore =
    filtros.tienda.length === 1 ? catalogoGlobal.find((s) => s.id === filtros.tienda[0]) ?? null : null;
  const selectedChain =
    filtros.cadena.length === 1
      ? cadenaToChain(filtros.cadena[0])
      : selectedStore
        ? storeChainBranch(selectedStore).chain
        : undefined;
  const selectedBranchOffice = selectedStore ? storeChainBranch(selectedStore).branchOffice : undefined;

  /**
   * Rótulo de las cards de negados: qué las acota y qué NO (12-oct). Son un
   * top 40 que antes se leía junto al total del embudo como si fuera lo mismo.
   */
  const alcanceNegados = (() => {
    const base = selectedStore
      ? selectedStore.tienda
      : filtros.cadena.length === 1
        ? `Cadena ${filtros.cadena[0]}`
        : 'Todas las tiendas';
    const noAcota: string[] = [];
    if (filtros.regional.length) noAcota.push('regional');
    if (filtros.supervisor.length) noAcota.push('supervisor');
    if (filtros.cadena.length > 1) noAcota.push('varias cadenas');
    if (filtros.tienda.length > 1) noAcota.push('varias tiendas');
    if (modo !== 'todas') noAcota.push('modo');
    return noAcota.length ? `${base} · no acota por ${noAcota.join(' ni ')}` : base;
  })();

  // ----------------------------- Vista PRESUPUESTO -----------------------------
  // Un solo fetch por (rango, cadena, sucursal). Regional/Supervisor/multi-
  // tienda NO viajan al servidor —el endpoint sólo acepta UNA cadena y UNA
  // sucursal, igual que el resto del Global— así que se acotan client-side
  // sobre `filteredStores`, que es la misma fuente que usa el embudo.
  const [meta, setMeta] = useState<MetaResponse | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [errorMeta, setErrorMeta] = useState<ErrorVista | null>(null);
  const [intentoMeta, setIntentoMeta] = useState(0);

  // Presupuesto muestra «Hoy (en vivo)» como el atajo de siempre: su periodo es
  // un rango, y el de un día de hoy se pinta como vivo en el panel.
  const esHoyMeta = rangoMeta.desde === rangoMeta.hasta && rangoMeta.desde === todayRange().desde;
  const periodoMeta: Periodo = esHoyMeta ? PERIODO_VIVO : periodoRango(rangoMeta);

  const aplicarMeta = ({ filtros: f, periodo: p }: FiltrosAplicados) => {
    setFiltros((prev) => (claveFiltros(prev) === claveFiltros(f) ? prev : f));
    const r = rangoEfectivo(p);
    setRangoMeta((prev) => (prev.desde === r.desde && prev.hasta === r.hasta ? prev : r));
  };

  useEffect(() => {
    if (view !== 'meta') return;
    let cancelado = false;
    setLoadingMeta(true);
    setErrorMeta(null);

    IndicadoresService.getMeta({
      desde: rangoMeta.desde,
      hasta: rangoMeta.hasta,
      chain: selectedChain,
      branchOffice: selectedBranchOffice,
    })
      .then((r) => {
        if (!cancelado) setMeta(r);
      })
      .catch((e: unknown) => {
        if (!cancelado) {
          setMeta(null);
          setErrorMeta(errorDe(e, 'No se pudo cargar el presupuesto'));
        }
      })
      .finally(() => {
        if (!cancelado) setLoadingMeta(false);
      });

    return () => {
      cancelado = true;
    };
  }, [view, rangoMeta.desde, rangoMeta.hasta, selectedChain, selectedBranchOffice, intentoMeta]);

  // Las filas que de verdad se pintan, ya acotadas por la cascada client-side.
  const metaFilas = useMemo(() => {
    if (!meta) return [];
    const permitidas = new Set(
      filteredStores.map((s) => {
        const { chain, branchOffice } = storeChainBranch(s);
        return rowKey(chain, branchOffice);
      }),
    );
    return meta.filas.filter((f) => permitidas.has(rowKey(f.chainId, f.branchOfficeId)));
  }, [meta, filteredStores]);

  // Los totales salen SIEMPRE de las filas visibles, nunca a veces de la API y
  // a veces de aquí: dos caminos es como la cabecera acaba diciendo una cosa y
  // la tabla otra. `meta.util.spec` fija que sobre el ámbito completo coinciden.
  const metaTotales = useMemo(() => totalizarMeta(metaFilas), [metaFilas]);

  const [negadosProductos, setNegadosProductos] = useState<NegadoProductoRow[]>([]);
  const [negadosTallas, setNegadosTallas] = useState<NegadoTallaRow[]>([]);
  const [loadingNegados, setLoadingNegados] = useState(true);
  const [errorNegados, setErrorNegados] = useState<ErrorVista | null>(null);
  const [intentoNegados, setIntentoNegados] = useState(0);
  const cargadoNegados = useRef<string | null>(null);

  // Drill "Detalle Negado" (PT7): fila de "Productos más negados" sobre la
  // que se hizo clic — mientras no sea `null`, `DetalleNegadoModal` se monta.
  const [detalleProducto, setDetalleProducto] = useState<NegadoProductoRow | null>(null);

  useEffect(() => {
    if (view !== 'global') return;
    const clave = [
      rangoGlobal.desde,
      rangoGlobal.hasta,
      selectedChain ?? '',
      selectedBranchOffice ?? '',
      claveEstatus,
      intentoNegados,
    ].join('|');
    if (cargadoNegados.current === clave) return;

    let cancelled = false;
    setLoadingNegados(true);
    setErrorNegados(null);

    // `chain` y `branchOffice` se pasan de forma independiente — `buildRangeQuery`
    // en el servicio omite cada uno por separado cuando viene `undefined`.
    const negadosFiltros: DashboardFiltros = {
      desde: rangoGlobal.desde,
      hasta: rangoGlobal.hasta,
      chain: selectedChain,
      branchOffice: selectedBranchOffice,
      status: filtros.estatus,
    };

    Promise.all([
      IndicadoresService.getNegadosProductos(negadosFiltros),
      IndicadoresService.getNegadosTallas(negadosFiltros),
    ])
      .then(([productos, tallas]) => {
        if (!cancelled) {
          setNegadosProductos(productos);
          setNegadosTallas(tallas);
          cargadoNegados.current = clave;
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorNegados(errorDe(err, 'No se pudo cargar negados.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingNegados(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    rangoGlobal.desde,
    rangoGlobal.hasta,
    selectedChain,
    selectedBranchOffice,
    claveEstatus,
    intentoNegados,
  ]);

  // ===== Vista Tienda: tienda + colaborador APLICADOS =====
  // Arranca en la default del handoff (`defaultStoreId`, línea 274). El panel
  // (`DrillTienda`) la cambia con «Aplicar»; el paso desde Global la fija si
  // el Global trae UNA tienda filtrada (Feedback B).
  const [miTiendaSelectedId, setMiTiendaSelectedId] = useState<string | null>(() =>
    defaultStoreId(catalogoStores),
  );
  // Ítem 11 (2026-07-28): filtro de COLABORADOR. Vacío = toda la tienda. El
  // roster del combo se guarda aparte (`vendedoresCatalogo`) porque la
  // respuesta YA viene acotada cuando hay filtro. MULTISELECT desde 2026-08-03.
  const [vendedorFiltro, setVendedorFiltro] = useState<string[]>(SIN_COLABORADORES);
  const [vendedoresCatalogo, setVendedoresCatalogo] = useState<{ id: string; nombre: string }[]>([]);
  const claveVendedores = claveLista(vendedorFiltro);

  // ⚠️ Con `catalogoStores` en las dependencias (12-oct): antes sólo dependía
  // del id y se quedaba con el objeto del catálogo del BUNDLE aunque llegara
  // el del backend, con su supervisor o regional corregidos.
  const miTiendaStore: Store | null = useMemo(() => {
    const id = miTiendaSelectedId ?? defaultStoreId(catalogoStores);
    return catalogoStores.find((s) => s.id === id) ?? null;
  }, [miTiendaSelectedId, catalogoStores]);

  /**
   * Cambia la tienda aplicada. El colaborador se limpia EN EL MISMO PASO: sus
   * `external_user_id` no existen en la tienda nueva. Antes se limpiaba en un
   * efecto aparte y salía un `/tienda` de más con los colaboradores viejos.
   */
  const fijarTienda = (storeId: string) => {
    if (storeId === miTiendaStore?.id) return;
    setMiTiendaSelectedId(storeId);
    setVendedorFiltro(SIN_COLABORADORES);
    setVendedoresCatalogo([]);
  };

  /** «Aplicar» del panel de Tienda: tienda, colaboradores y periodo de una vez. */
  const aplicarTienda = ({ storeId, vendedorId, periodo: p }: TiendaAplicada) => {
    const cambiaTienda = storeId !== miTiendaStore?.id;
    if (cambiaTienda) {
      fijarTienda(storeId);
    } else {
      const siguiente = vendedorId.length ? vendedorId : SIN_COLABORADORES;
      setVendedorFiltro((prev) => (claveLista(prev) === claveLista(siguiente) ? prev : siguiente));
    }
    setPeriodo((prev) => (mismoPeriodo(prev, p) ? prev : p));
  };

  const [tienda, setTienda] = useState<TiendaResponse | null>(null);
  const [loadingTienda, setLoadingTienda] = useState(false);
  const [errorTienda, setErrorTienda] = useState<ErrorVista | null>(null);
  const [intentoTienda, setIntentoTienda] = useState(0);

  // `false` mientras quede el (único) reintento de `clientesNuevosPendiente`
  // para la tienda/periodo ACTUAL; se resetea con cada fetch nuevo.
  const [clientesNuevosRetryDone, setClientesNuevosRetryDone] = useState(false);

  // Hora "ahora" (Task 9): SIEMPRE en zona horaria America/Mexico_City (las
  // tiendas operan en hora MX sin importar en qué huso corra el navegador).
  // Se calcula UNA sola vez al montar la pantalla.
  const [nowHour] = useState(() => getNowHourMx());

  const clavePeriodoAplicado = clavePeriodo(periodo);

  // Fetch de `GET /dashboard/tienda` — sólo con la vista Tienda activa. Se
  // re-dispara al aplicar otra tienda, otro colaborador u otro periodo, o al
  // volver a entrar a la vista (datos frescos en vivo).
  useEffect(() => {
    if (view !== 'tienda' || !miTiendaStore) {
      return;
    }

    let cancelled = false;
    setLoadingTienda(true);
    setErrorTienda(null);
    setClientesNuevosRetryDone(false);

    const { chain, branchOffice } = storeChainBranch(miTiendaStore);
    // En vivo, SIN fechas: la API responde hoy vs ayer (`rango: null`).
    const desde = periodo.modo === 'rango' ? periodo.desde : undefined;
    const hasta = periodo.modo === 'rango' ? periodo.hasta : undefined;
    IndicadoresService.getTienda(chain, branchOffice, desde, hasta, vendedorFiltro)
      .then((data) => {
        if (!cancelled) {
          setTienda(data);
          // El roster del combo se actualiza SOLO con respuestas sin filtro
          // (las filtradas traen sólo a los elegidos — ver `vendedoresCatalogo`).
          if (vendedorFiltro.length === 0) {
            setVendedoresCatalogo(
              data.vendedores.map((v) => ({
                id: v.externalUserId,
                // `nombre` puede venir null (vendedor sin nombre en Proscai):
                // el combo cae al AGSEQ para que siga siendo seleccionable.
                nombre: v.nombre ?? v.externalUserId,
              })),
            );
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorTienda(errorDe(err, 'No se pudo cargar la tienda.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTienda(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, miTiendaStore?.id, clavePeriodoAplicado, claveVendedores, intentoTienda]);

  // ===== Reintento automático de `clientesNuevosPendiente` (a lo más UNO) =====
  // Cuando `/tienda` resuelve con `clientesNuevosPendiente`, se agenda UN
  // reintento 15 s después con la MISMA tienda/periodo/colaborador. Si vuelve
  // pendiente, la tarjeta se rinde ("N/D") sin seguir reintentando.
  useEffect(() => {
    let cancelled = false;

    if (
      loadingTienda ||
      errorTienda ||
      !tienda?.clientesNuevosPendiente ||
      clientesNuevosRetryDone ||
      view !== 'tienda' ||
      !miTiendaStore
    ) {
      return () => {
        cancelled = true;
      };
    }

    const { chain, branchOffice } = storeChainBranch(miTiendaStore);
    const desde = periodo.modo === 'rango' ? periodo.desde : undefined;
    const hasta = periodo.modo === 'rango' ? periodo.hasta : undefined;
    const timer = setTimeout(() => {
      IndicadoresService.getTienda(chain, branchOffice, desde, hasta, vendedorFiltro)
        .then((data) => {
          if (!cancelled) {
            setTienda(data);
          }
        })
        .catch(() => {
          // Se rinde en silencio: sólo deja de reintentar.
        })
        .finally(() => {
          if (!cancelled) {
            setClientesNuevosRetryDone(true);
          }
        });
    }, CLIENTES_NUEVOS_RETRY_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    miTiendaStore?.id,
    clavePeriodoAplicado,
    claveVendedores,
    tienda?.clientesNuevosPendiente,
    clientesNuevosRetryDone,
    loadingTienda,
    errorTienda,
  ]);

  // ===== Tabla "Productos más negados" de la tienda (Ajuste B #4) =====
  // Fetch INDEPENDIENTE de `/dashboard/tienda` (propio loading/error).
  // `/dashboard/negados-productos` siempre exige un rango: en vivo, hoy.
  const [negadosTienda, setNegadosTienda] = useState<NegadoProductoRow[]>([]);
  const [loadingNegadosTienda, setLoadingNegadosTienda] = useState(false);
  const [errorNegadosTienda, setErrorNegadosTienda] = useState<ErrorVista | null>(null);
  const [intentoNegadosTienda, setIntentoNegadosTienda] = useState(0);

  // Drill "Negados por talla/día" de la tienda (Ajuste B #5, AD-3).
  const [negadoDrillProductId, setNegadoDrillProductId] = useState<string | null>(null);

  // Detalle/trazabilidad por producto del vendedor (Task FE-C).
  const [vendedorDetalleId, setVendedorDetalleId] = useState<string | null>(null);

  // Rango efectivo de Tienda para los endpoints que SIEMPRE requieren rango
  // (`negados-productos`, `negados-detalle`, `vendedor-detalle`): en vivo, hoy.
  // Los modales leen ESTE rango, el aplicado.
  const negadosRango = rangoEfectivo(periodo, hoy);

  useEffect(() => {
    if (view !== 'tienda' || !miTiendaStore) {
      return;
    }

    let cancelled = false;
    setLoadingNegadosTienda(true);
    setErrorNegadosTienda(null);

    const { chain, branchOffice } = storeChainBranch(miTiendaStore);

    IndicadoresService.getNegadosProductos({
      desde: negadosRango.desde,
      hasta: negadosRango.hasta,
      chain,
      branchOffice,
    })
      .then((rows) => {
        if (!cancelled) {
          setNegadosTienda(rows);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorNegadosTienda(errorDe(err, 'No se pudo cargar los negados de la tienda.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingNegadosTienda(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, miTiendaStore?.id, negadosRango.desde, negadosRango.hasta, intentoNegadosTienda]);

  // ===== La cabecera FIJA de la aplicación vive FUERA de este módulo =====
  // `AdminLayout` monta `<header class="fixed top-0 … z-50">` por encima de
  // todo (73 px medidos). El `<main>` que envuelve al módulo separa 80 px, así
  // que en móvil este contenedor separa 20 px por arriba en vez de 14: el
  // tablero arranca 27 px por debajo de la cabecera y las pestañas del `Header`
  // quedan libres para el dedo.
  return (
    <div
      style={{
        minHeight: '100%',
        width: '100%',
        // `width: 100%` + padding sólo cabe si la caja mide por el borde. Hoy
        // lo garantiza el preflight de Tailwind (`*{box-sizing:border-box}`),
        // que es CSS de otro módulo: dejarlo escrito aquí evita que el marco
        // entero se salga 24-32 px si ese preflight se mueve de sitio.
        boxSizing: 'border-box',
        background: colors.bg,
        // Teléfono: CERO margen lateral aquí; los 12 px ya los pone `AdminLayout`.
        padding: esMovil ? '20px 0 24px' : '14px 16px 26px',
        fontFamily,
        color: colors.texto.primary,
      }}
    >
      {/* @keyframes cmpulse/cmfade son globales al documento; se inyectan
          una sola vez desde el shell. */}
      <style>{keyframesCss}</style>

      <Header
        view={view}
        onGlobal={() => setView('global')}
        onTienda={() => {
          // El PERIODO no se copia: es el mismo estado en las dos vistas.
          // Feedback B (2026-07-28): si el Global trae UNA tienda filtrada,
          // Tienda abre en ESA (sin una tienda concreta conserva la suya).
          // Una tienda CERRADA no se abre en Tienda (fuera de este paso, SPEC
          // 2026-10-13): Tienda conserva la suya.
          if (selectedStore && !selectedStore.cerrada) {
            fijarTienda(selectedStore.id);
          }
          setView('tienda');
        }}
        onMeta={
          PESTANA_PRESUPUESTO_LANZADA && hayPresupuesto
            ? () => setView('meta')
            : undefined
        }
        modo={modo}
        onModoChange={setModo}
        periodo={view === 'meta' ? periodoRango(rangoMeta) : periodo}
      />

      {view === 'global' && (
        <div style={{ animation: 'cmfade .3s ease both' }}>
          <Filtros
            stores={catalogoGlobal}
            filtros={filtros}
            periodo={periodo}
            onAplicar={aplicarGlobal}
            modo={modo}
            activeStoreKeys={activeStoreKeys}
          />

          {/* Frescura del "en vivo". Sólo en vivo: para un rango pasado la
              pregunta no aplica.

              Existe porque el 2026-09-03 la venta del día se quedó congelada 7
              horas mostrando el 26 % de lo real y **nadie podía verlo**. Si no
              hay materializado, se dice que va en vivo contra el ERP — nunca se
              calla de dónde viene el número. */}
          {enVivo && frescuraHoy !== undefined && (
            <div
              data-testid="frescura-en-vivo"
              style={{
                marginTop: esMovil ? 10 : 14,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: colors.card,
                border: `1px solid ${colors.border}`,
                borderRadius: 999,
                padding: esMovil ? '7px 13px' : '6px 14px',
                fontSize: esMovil ? 12.5 : 12,
                fontWeight: 600,
                color: colors.texto.secondary,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: frescuraHoy?.fresco ? colors.verde : colors.dorado,
                  animation: frescuraHoy?.fresco ? 'cmpulse 1.6s infinite' : undefined,
                  flex: '0 0 auto',
                }}
              />
              {frescuraHoy
                ? `Venta actualizada ${textoFrescura(frescuraHoy.minutos)}`
                : 'Venta en vivo desde el ERP'}
            </div>
          )}

          <div
            style={{
              // 12 px entre bloques en teléfono (20 px es aire de escritorio).
              marginTop: esMovil ? 12 : 20,
              display: 'flex',
              alignItems: 'baseline',
              gap: esMovil ? 8 : 12,
              flexWrap: 'wrap',
            }}
          >
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: '.01em' }}>
              Embudo operativo del periodo
            </h2>
            <span style={{ fontSize: 12.5, color: '#8b909b', fontWeight: 500 }}>
              Consulta de talla → solicitud a bodega → entrega del bodeguero → venta
            </span>
            <span
              data-testid="scope-chip"
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: colors.brand,
                background: colors.brandSoftBg,
                border: `1px solid ${colors.brandSoftBorder}`,
                borderRadius: 999,
                padding: '3px 11px',
              }}
            >
              {scopeLabel}
            </span>
            {/* Si la API del catálogo falló se usa el del bundle, que puede
                estar desfasado. Decirlo: un dato viejo sin avisar es peor que
                no tener dato. */}
            {catalogoDeRespaldo && !cargandoCatalogo && (
              <span
                title="No se pudo consultar el catálogo de tiendas; se está usando la copia local, que puede estar desactualizada."
                style={{
                  fontSize: 12,
                  color: '#92400e',
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: 999,
                  padding: '3px 11px',
                  marginLeft: 8,
                }}
              >
                catálogo local (sin conexión)
              </span>
            )}
          </div>

          {loadingSucursales && (
            <EstadoCarga testid="embudo-loading" texto="Cargando datos del periodo…" marginTop={11} />
          )}

          {!loadingSucursales && errorSucursales && (
            <EstadoError
              testid="embudo-error"
              titulo="No se pudo cargar el periodo"
              mensaje={errorSucursales.mensaje}
              validacion={errorSucursales.validacion}
              onReintentar={() => setIntentoSucursales((n) => n + 1)}
              marginTop={11}
            />
          )}

          {!loadingSucursales && !errorSucursales && (
            <>
              <Embudo
                consultas={aggregate.C}
                solicitudes={aggregate.S}
                atendidas={aggregate.A}
                venta={aggregate.V}
                negados={aggregate.N}
                efect={aggregate.efect}
                ventaUnavailable={aggregate.ventaUnavailable}
                ventaCalculando={ventaCalculando}
                ventaDeTodoElAmbito={modo !== 'todas'}
              />
              <Medidores efect={aggregate.efect} sla={aggregate.sla} tasa={aggregate.tasa} />
              {/* La venta, las altas y los estrenos: de la cascada, SIN modo. */}
              <VentaCards
                testid="venta-cards-global"
                contadoPares={aggregate.vContadoPares}
                creditoPares={aggregate.vCreditoPares}
                contadoMonto={aggregate.vContadoMonto}
                creditoMonto={aggregate.vCreditoMonto}
                ventaUnavailable={aggregate.ventaUnavailable}
                ventaCalculando={ventaCalculando}
                descuento={aggregate.vDescuento}
                prestamos={aggregate.vPrestamos}
                clientesNuevos={aggregate.clientesNuevos}
                clientesNuevosConVenta={aggregate.clientesNuevosConVenta}
                clientesNuevosPendiente={aggregate.clientesNuevosPendiente}
                clientesNuevosCalculando={aggregate.clientesNuevosPendiente && reintentosVivos}
                notaClientes={NOTA_CLIENTES_GLOBAL}
                tickets={aggregate.tickets}
                ticketsPrestamo={aggregate.ticketsPrestamo}
                procedenciaClientes={aggregate.procedenciaClientes}
                rango={enVivo ? null : rangoGlobal}
              />
              <TablaSucursales
                rows={sucursalTable.rows}
                rowsAll={sucursalTable.rowsAll}
                total={sucursalTable.total}
                onOpenStore={handleOpenStore}
                ventaUnavailable={aggregate.ventaUnavailable}
                ventaCalculando={ventaCalculando}
              />
            </>
          )}

          {/* Cards de negados: fetch INDEPENDIENTE (propio loading/error). Un
              error NO se pinta como «sin negados». */}
          {loadingNegados && (
            <EstadoCarga testid="negados-loading" texto="Cargando negados del periodo…" />
          )}

          {!loadingNegados && errorNegados && (
            <EstadoError
              testid="negados-error"
              titulo="No se pudo cargar negados"
              mensaje={errorNegados.mensaje}
              validacion={errorNegados.validacion}
              onReintentar={() => setIntentoNegados((n) => n + 1)}
            />
          )}

          {!loadingNegados && !errorNegados && (
            <NegadosCards
              productos={negadosProductos}
              tallas={negadosTallas}
              onSelectProducto={setDetalleProducto}
              alcance={alcanceNegados}
            />
          )}

          {/* Drill "Detalle Negado" (PT7): hereda el rango + estatus + alcance
              APLICADOS, para que cuadre con lo que la tabla está mostrando. */}
          {detalleProducto && (
            <DetalleNegadoModal
              producto={detalleProducto}
              filtros={{
                desde: rangoGlobal.desde,
                hasta: rangoGlobal.hasta,
                chain: selectedChain,
                branchOffice: selectedBranchOffice,
                status: filtros.estatus,
              }}
              onClose={() => setDetalleProducto(null)}
            />
          )}
        </div>
      )}

      {view === 'meta' && (
        <div data-testid="vista-meta" style={{ animation: 'cmfade .3s ease both' }}>
          <Filtros
            stores={catalogoStores}
            filtros={filtros}
            periodo={periodoMeta}
            onAplicar={aplicarMeta}
            mostrarEstatus={false}
            rangosDeMeta
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              // Sin `minWidth: 0` la tabla de presupuesto ensancharía este
              // bloque y volvería a arrastrar la página de lado.
              minWidth: 0,
              gap: esMovil ? 12 : 16,
              marginTop: esMovil ? 12 : 16,
            }}
          >
            {errorMeta && (
              <EstadoError
                testid="meta-error"
                titulo="No se pudo cargar el presupuesto"
                mensaje={errorMeta.mensaje}
              validacion={errorMeta.validacion}
                onReintentar={() => setIntentoMeta((n) => n + 1)}
                marginTop={0}
              />
            )}

            {loadingMeta && meta && (
              <div
                data-testid="meta-actualizando"
                style={{
                  alignSelf: 'flex-start',
                  background: colors.brandSoftBg,
                  border: `1px solid ${colors.brandSoftBorder}`,
                  borderRadius: 999,
                  padding: '5px 14px',
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: colors.brand,
                }}
              >
                Actualizando el periodo…
              </div>
            )}

            {loadingMeta && !meta && (
              <div
                style={{
                  // 40 px por lado son casi un cuarto de un teléfono para una
                  // sola línea de texto.
                  padding: esMovil ? '28px 14px' : 40,
                  textAlign: 'center',
                  color: colors.texto.tertiary,
                }}
              >
                Cargando presupuesto…
              </div>
            )}

            {/* ⚠️ Mientras recarga se atenúa TODO el bloque. Sin esto, cambiar
                de periodo dejaba las FECHAS nuevas arriba y las CIFRAS viejas
                debajo durante un par de segundos. */}
            {meta && (
              <div
                data-testid="meta-contenido"
                aria-busy={loadingMeta}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  gap: esMovil ? 12 : 16,
                  opacity: loadingMeta ? 0.45 : 1,
                  transition: 'opacity .2s ease',
                  pointerEvents: loadingMeta ? 'none' : undefined,
                }}
              >
                {/* El porcentaje se APAGA cuando la cobertura no está completa:
                    si falta un día de meta, no hay porcentaje que publicar. */}
                <AvisoCobertura cobertura={meta.cobertura} />
                <AvanceMeta
                  metaMonto={metaTotales.metaMonto}
                  ventaMonto={metaTotales.ventaMonto}
                  faltanteMonto={metaTotales.faltanteMonto}
                  cumplimientoMonto={
                    meta.cobertura.completa ? metaTotales.cumplimientoMonto : null
                  }
                  metaPares={metaTotales.metaPares}
                  ventaPares={metaTotales.ventaPares}
                  faltantePares={metaTotales.faltantePares}
                  cumplimientoPares={
                    meta.cobertura.completa ? metaTotales.cumplimientoPares : null
                  }
                  ritmoEsperado={meta.totales.ritmoEsperado}
                  diasTranscurridos={meta.totales.diasTranscurridos}
                  diasDelPeriodo={meta.totales.diasDelPeriodo}
                />
                <MedidoresMeta
                  cumplimientoMonto={
                    meta.cobertura.completa ? metaTotales.cumplimientoMonto : null
                  }
                  cumplimientoPares={
                    meta.cobertura.completa ? metaTotales.cumplimientoPares : null
                  }
                  filas={metaFilas}
                />
                <GraficoMeta serie={meta.serieDiaria} hoy={meta.rango.hoy} />
                <TablaMeta filas={metaFilas} tiendasEsperadas={metaFilas.length} />
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'tienda' && (
        <div
          data-testid="vista-tienda"
          data-negado-drill-productid={negadoDrillProductId ?? ''}
          style={{ animation: 'cmfade .3s ease both', marginTop: esMovil ? 12 : 16 }}
        >
          {miTiendaStore && (
            <DrillTienda
              stores={catalogoStores}
              store={miTiendaStore}
              periodo={periodo}
              lastRefresh={tienda?.lastRefresh ?? null}
              vendedores={vendedoresCatalogo}
              vendedorId={vendedorFiltro}
              onAplicar={aplicarTienda}
            />
          )}

          {loadingTienda && <EstadoCarga testid="tienda-loading" texto="Cargando datos de la tienda…" />}

          {!loadingTienda && errorTienda && (
            <EstadoError
              testid="tienda-error"
              titulo="No se pudo cargar la tienda"
              mensaje={errorTienda.mensaje}
              validacion={errorTienda.validacion}
              onReintentar={() => setIntentoTienda((n) => n + 1)}
            />
          )}

          {!loadingTienda && !errorTienda && tienda && (
            <>
              <KpisHoy hoy={tienda.hoy} ayer={tienda.ayer} comparativo={enVivo ? 'ayer' : 'anterior'} />

              {/* Las MISMAS tarjetas de venta que Global, Descuento y
                  Préstamos incluidos (decisión del usuario, 12-oct). */}
              <VentaCards
                testid="venta-cards-tienda"
                contadoPares={tienda.hoy.ventaContadoPares}
                creditoPares={tienda.hoy.ventaCreditoPares}
                contadoMonto={tienda.hoy.ventaContadoMonto}
                creditoMonto={tienda.hoy.ventaCreditoMonto}
                ventaUnavailable={tienda.hoy.venta === null}
                descuento={tienda.hoy.ventaDescuento}
                prestamos={tienda.hoy.ventaPrestamos}
                mostrador={{
                  pares: tienda.hoy.ventaMostradorPares ?? 0,
                  monto: tienda.hoy.ventaMostradorMonto ?? 0,
                }}
                clientesNuevos={tienda.hoy.clientesNuevos}
                clientesNuevosConVenta={tienda.hoy.clientesNuevosConVenta}
                clientesNuevosPendiente={tienda.clientesNuevosPendiente}
                clientesNuevosCalculando={tienda.clientesNuevosPendiente && !clientesNuevosRetryDone}
                notaTotal={NOTA_TOTAL_TIENDA}
                notaClientes={NOTA_CLIENTES_TIENDA}
                tickets={tienda.hoy.tickets}
                ticketsPrestamo={tienda.hoy.ticketsPrestamo}
                procedenciaClientes={
                  tienda.clientesNuevosFuente
                    ? {
                        fuente: tienda.clientesNuevosFuente,
                        cierre: tienda.clientesNuevosCierre ?? null,
                        deriva: tienda.clientesNuevosDeriva ?? null,
                        correo: null,
                      }
                    : undefined
                }
                rango={tienda.rango}
                clientesSinVendedor={tienda.hoy.clientesNuevosSinVendedor}
              />

              {/* Gráfica por hora + bodega, justo después de los KPIs y antes
                  de Vendedores. En móvil una sola columna que SÍ puede
                  encoger (`minmax(0, 1fr)`). */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: esMovil
                    ? 'minmax(0, 1fr)'
                    : 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: esMovil ? 12 : 14,
                  marginTop: esMovil ? 12 : 14,
                }}
              >
                <GraficoHora porHora={tienda.porHora} nowHour={nowHour} mostrarAhora={enVivo} />
                <BodegaCard bodega={tienda.bodega} enVivo={enVivo} />
              </div>

              {/* Ranking de vendedores (Task 10): refleja el periodo aplicado. */}
              <Vendedores
                vendedores={tienda.vendedores}
                porHora={tienda.porHora}
                goal={CONVERSION_GOAL}
                clientesNuevosPendiente={tienda.clientesNuevosPendiente}
                onSelectVendedor={setVendedorDetalleId}
                rango={tienda.rango}
              />

              {/* "Productos más negados" de la tienda: fetch INDEPENDIENTE. */}
              {loadingNegadosTienda && (
                <EstadoCarga
                  testid="negados-tienda-loading"
                  texto="Cargando negados de la tienda…"
                  marginTop={18}
                />
              )}

              {!loadingNegadosTienda && errorNegadosTienda && (
                <EstadoError
                  testid="negados-tienda-error"
                  titulo="No se pudo cargar los negados de la tienda"
                  mensaje={errorNegadosTienda.mensaje}
              validacion={errorNegadosTienda.validacion}
                  onReintentar={() => setIntentoNegadosTienda((n) => n + 1)}
                  marginTop={18}
                />
              )}

              {!loadingNegadosTienda && !errorNegadosTienda && (
                <NegadosTiendaTable
                  productos={negadosTienda}
                  onSelectProducto={setNegadoDrillProductId}
                  rango={tienda.rango}
                  conColaborador={vendedorFiltro.length > 0}
                />
              )}
            </>
          )}

          {/* Drill "Negados por talla/día" (AD-3): hereda el rango + tienda
              APLICADOS. No depende de `tienda`: puede abrirse aunque ese bloque
              siga cargando, con tal de que haya una tienda elegida. */}
          {negadoDrillProductId && miTiendaStore && (
            <NegadosDrillModal
              productId={negadoDrillProductId}
              descripcion={
                negadosTienda.find((p) => p.productId === negadoDrillProductId)?.descripcion ?? ''
              }
              filtros={{
                desde: negadosRango.desde,
                hasta: negadosRango.hasta,
                chain: storeChainBranch(miTiendaStore).chain,
                branchOffice: storeChainBranch(miTiendaStore).branchOffice,
              }}
              onClose={() => setNegadoDrillProductId(null)}
            />
          )}

          {/* Detalle/trazabilidad por producto del vendedor (Task FE-C):
              rango + tienda APLICADOS; nombre/código de `tienda.vendedores`. */}
          {vendedorDetalleId && miTiendaStore && (
            <VendedorDetalleModal
              vendedorId={vendedorDetalleId}
              nombre={
                tienda?.vendedores.find((v) => v.externalUserId === vendedorDetalleId)?.nombre ??
                undefined
              }
              codigo={tienda?.vendedores.find((v) => v.externalUserId === vendedorDetalleId)?.codigo}
              filtros={{
                desde: negadosRango.desde,
                hasta: negadosRango.hasta,
                chain: storeChainBranch(miTiendaStore).chain,
                branchOffice: storeChainBranch(miTiendaStore).branchOffice,
              }}
              onClose={() => setVendedorDetalleId(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default IndicadoresScreen;
